// /prep/<slug>: one prep, full page, the operator's. The editor takes the
// width with the playbooks rail in a column beside it that can be dragged
// wider or narrower; on a phone the editor has the screen and a Playbooks
// button opens the rail as a sheet. With BREEZE_PANEL_ENABLED the column is
// the Breeze chat, as it was before 2026-10-31.

import { useCallback, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from "react";
import { ROOT_HANDLE, parseListenQuery, type AccountUser } from "@breeze/core";
import { BREEZE_PANEL_ENABLED } from "../lib/playbooks";
import { PlaybookRail } from "../components/PlaybookRail";
import { useMediaQuery, WIDE_QUERY } from "../hooks/useMediaQuery";
import { navigate } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";
import { BackIcon } from "../components/icons";
import { ListenInline } from "../components/ListenInline";
import { ListeningIndicator, useListeningStatus } from "../components/ListeningIndicator";
import { NothingHere } from "../components/NothingHere";
import { PrepChat } from "../components/PrepChat";
import { PrepEditor } from "../components/PrepEditor";
import { WorkspaceSheet } from "../components/WorkspaceSheet";

const WIDTH_KEY = "breeze:prep-page-chat";
const MIN_W = 280;
const MAX_W = 640;
const DEFAULT_W = 380;

function readWidth(): number {
  try {
    const v = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(v) && v >= MIN_W && v <= MAX_W) return v;
  } catch {
    // Storage is blocked: the default holds.
  }
  return DEFAULT_W;
}

export function PrepScreen({ slug, me }: { readonly slug: string; readonly me: AccountUser }): JSX.Element {
  // The page's own address can carry who the call is with and where it runs
  // (?guest=Daniel%20Stark&app=Teams), so Listen here labels their side.
  const listenTarget = useRef({ ...parseListenQuery(window.location.search), prepSlug: slug }).current;
  const [listening, setListening] = useState(false);
  const elsewhere = useListeningStatus().status;
  const wide = useMediaQuery(WIDE_QUERY);
  const [missing, setMissing] = useState(false);
  const [join, setJoin] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [sheet, setSheet] = useState(false);
  const [width, setWidth] = useState(readWidth);
  // The playbooks rail stands where Breeze stood, for the operator only.
  const books = !BREEZE_PANEL_ENABLED && me.handle.toLowerCase() === ROOT_HANDLE;

  const onChanged = useCallback(() => setRefreshKey((n) => n + 1), []);
  const onOpen = useCallback((next: string) => navigate(`/prep/${encodeURIComponent(next)}`), []);

  const startResize = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    let last = 0;
    const move = (ev: PointerEvent): void => {
      const w = Math.max(MIN_W, Math.min(MAX_W, window.innerWidth - ev.clientX, window.innerWidth * 0.55));
      last = w;
      setWidth(w);
    };
    const up = (): void => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      if (last) {
        try {
          localStorage.setItem(WIDTH_KEY, String(Math.round(last)));
        } catch {
          // Not remembered.
        }
      }
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }, []);

  if (missing) return <NothingHere />;

  const back = (
    <a
      className="pg__back"
      href={ownRoomPath()}
      onClick={(e) => {
        e.preventDefault();
        navigate(ownRoomPath());
      }}
    >
      <BackIcon size={18} />
      <span>Room</span>
    </a>
  );

  return (
    <div className="pg">
      <div className="pg__main">
        <div className="pg__bar">
          {back}
          {join ? (
            <a
              className="pg__join"
              data-testid="prep-join"
              href={join}
              {...(/^https?:/i.test(join) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {/^tel:/i.test(join) ? "Call" : "Join"}
            </a>
          ) : null}
          {!listening && !elsewhere ? (
            <button type="button" className="pg__listen-btn" data-testid="prep-listen" onClick={() => setListening(true)}>
              Listen
            </button>
          ) : null}
        </div>
        {listening ? (
          <div className="pg__listen">
            <ListenInline target={listenTarget} me={me} onStop={() => setListening(false)} />
          </div>
        ) : (
          <ListeningIndicator />
        )}
        <div className="pg__editor">
          <PrepEditor slug={slug} refreshKey={refreshKey} onMissing={() => setMissing(true)} onJoin={setJoin} />
        </div>
      </div>
      {wide && (books || BREEZE_PANEL_ENABLED) ? (
        <>
          <div
            className="pg__resize"
            role="separator"
            aria-orientation="vertical"
            aria-label={books ? "Resize playbooks" : "Resize Breeze"}
            onPointerDown={startResize}
            onDoubleClick={() => {
              setWidth(DEFAULT_W);
              try {
                localStorage.removeItem(WIDTH_KEY);
              } catch {
                // Nothing stored to clear.
              }
            }}
          />
          {books ? (
            <aside className="pg__chat" style={{ width }} aria-label="Playbooks">
              <div className="pw pg__books">
                <PlaybookRail callSlug={slug} shut={false} fold={null} />
              </div>
            </aside>
          ) : BREEZE_PANEL_ENABLED ? (
            <aside className="pg__chat" style={{ width }} aria-label="Breeze">
              <h2 className="pg__chat-title">Breeze</h2>
              <PrepChat slug={slug} onChanged={onChanged} onOpen={onOpen} />
            </aside>
          ) : null}
        </>
      ) : books ? (
        <>
          <button type="button" className="pg__fab" onClick={() => setSheet(true)}>
            Playbooks
          </button>
          {sheet ? (
            <WorkspaceSheet title="Playbooks" onClose={() => setSheet(false)} tall>
              <div className="pw pg__books">
                <PlaybookRail callSlug={slug} shut={false} fold={null} />
              </div>
            </WorkspaceSheet>
          ) : null}
        </>
      ) : !BREEZE_PANEL_ENABLED ? null : (
        <>
          <button type="button" className="pg__fab" onClick={() => setSheet(true)}>
            Breeze
          </button>
          {sheet ? (
            <WorkspaceSheet title="Breeze" onClose={() => setSheet(false)} tall>
              <PrepChat
                slug={slug}
                onChanged={onChanged}
                onOpen={(next) => {
                  setSheet(false);
                  onOpen(next);
                }}
              />
            </WorkspaceSheet>
          ) : null}
        </>
      )}
    </div>
  );
}
